/* ============================================================
   EPG — the pull-up TV guide, as a real time-based grid.
   A wall-clock horizontal axis (~30 min visible); each channel's
   programs are duration-proportional blocks aligned to the same
   broadcast schedule (live.ts). One red now-line sweeps across all
   rows at the current broadcast moment.

   Cursor = (selected channel guideSel, selected video guideProgram).
   ↑/↓ moves channel; ←/→ steps the video cursor (broadcast order);
   OK/click tunes — going LIVE on the now-airing block, or playing a
   future block from its start.
   ============================================================ */
import { useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react'
import { useTv } from '@/tv/context'
import { queueIndexToProgramOrdinal, programOrdinalToQueueIndex } from '@/tv/breakResolver'
import { isProgram } from '@/tv/itemKinds'
import { buildLiveQueue, liveSlot, programsInWindow, nearestAiringMs, itemDurationSeconds, GUIDE_ZOOMS, type ScheduledItem } from '@/tv/live'
import { searchGuide, type SearchResult } from '@/tv/guideSearch'
import type { QueueItem } from '@/tv/types'
import styles from './Epg.module.css'

const pad = (n: number) => (n < 10 ? '0' : '') + n
const LEAD_FRAC = 0.12 // fraction of the window shown left of "now"
const REPIN_FRAC = 0.85 // re-anchor the window once now sweeps this far across

// Axis/block labels show seconds only when zoomed in tight enough to need them.
const timeLabel = (ms: number, withSeconds: boolean) => {
  const d = new Date(ms)
  const base = pad(d.getHours()) + ':' + pad(d.getMinutes())
  return withSeconds ? base + ':' + pad(d.getSeconds()) : base
}
const anchorFor = (now: number, spanMs: number) => now - LEAD_FRAC * spanMs

export function Epg() {
  const tv = useTv()
  const { state, channels } = tv
  const { guideOpen, channelIndex, guideSel, guideProgram, guideZoom, guideSearch, guideSearchSel } = state
  const { spanMs, tickMs } = GUIDE_ZOOMS[guideZoom] ?? GUIDE_ZOOMS[GUIDE_ZOOMS.length >> 1]

  // Per-channel broadcast queues — honour the ad-bumpers setting so the guide shows the SAME
  // schedule that plays (no phantom ad breaks / split programs when ads are off). Rebuilt when the
  // lineup OR the setting changes.
  const queues = useMemo(() => {
    const m = new Map<number, QueueItem[]>()
    for (const ch of channels) m.set(ch.number, buildLiveQueue(ch, state.settings.adBumpers))
    return m
  }, [channels, state.settings.adBumpers])

  // eslint-disable-next-line no-restricted-syntax -- view-only refs to scroll the selected row WITHIN the guide body; see NOTE below
  const selRowRef = useRef<HTMLDivElement>(null)
  // eslint-disable-next-line no-restricted-syntax -- view-only ref; see NOTE below
  const bodyRef = useRef<HTMLDivElement>(null)
  // The airing (wall-clock window) of the currently-selected program — what the guide must keep in
  // view. Null for a no-signal/empty channel (no schedule). Recomputed on demand inside the effects.
  const selectedAiring = (atMs: number): { startMs: number; endMs: number } | null => {
    const ch = channels[guideSel]
    const q = ch ? queues.get(ch.number) : undefined
    if (!ch || ch.noSignal || !q || !q.length) return null
    const qi = programOrdinalToQueueIndex(q, guideProgram)
    const start = nearestAiringMs(q, qi, atMs)
    return start == null ? null : { startMs: start, endMs: start + itemDurationSeconds(q[qi]) * 1000 }
  }

  // NOTE — local time state is a DELIBERATE exception to the stateless-component rule here.
  // The EPG is a pure time-grid VIEW: the window anchor and the 1s freshness ticker are
  // render-only concerns with no bearing on playback, and the store deliberately holds NO clock
  // and no ticking dispatch (see TvProvider.tsx — "the store holds no clock"). Keeping this view's
  // wall-clock math local honors that decision; the eslint-disables below are scoped to it.
  // Authorized by the repo owner (João Anes) on 2026-06-03, session
  // 8b3047fa-52b0-4cd0-80c7-172582b058e8.

  // The fixed window start. Re-anchors on open, on zoom, and once the now-line
  // nears the edge — keeping "now" in view at a steady lead.
  // eslint-disable-next-line no-restricted-syntax -- view-only window anchor; see NOTE above
  const [viewStart, setViewStart] = useState(() => anchorFor(Date.now(), spanMs))
  // re-anchor on open / zoom / SELECTION change so the selected program is always brought into view
  // (stepping the cursor past the visible edge scrolls the time window to follow it).
  useEffect(() => {
    if (!guideOpen) return
    const sel = selectedAiring(Date.now())
    // eslint-disable-next-line react-hooks/set-state-in-effect -- re-anchor the view window; see NOTE above
    setViewStart(anchorFor(sel ? sel.startMs : Date.now(), spanMs))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- channels/queues are stable; follow selection
  }, [guideOpen, guideZoom, spanMs, guideSel, guideProgram])

  // keep the selected channel ROW scrolled into view (rows can overflow the body vertically)
  useEffect(() => {
    // Scroll ONLY the guide body (never ancestors — scrollIntoView would scroll the screen/content
    // and leave it offset on close). No-op when the body doesn't overflow.
    const row = selRowRef.current
    const body = bodyRef.current
    if (!guideOpen || !row || !body || body.scrollHeight <= body.clientHeight) return
    const r = row.getBoundingClientRect()
    const b = body.getBoundingClientRect()
    if (r.top < b.top) body.scrollTop -= b.top - r.top
    else if (r.bottom > b.bottom) body.scrollTop += r.bottom - b.bottom
  }, [guideSel, guideProgram, guideOpen])
  // 1s ticker while open: keeps `now` second-fresh (the now-block highlight and
  // the now-line stay in sync with the real broadcast clock) and re-anchors the
  // window as the now-line nears the edge. The header HH:MM `clock` only changes
  // each minute, so it can't drive this on its own.
  // eslint-disable-next-line no-restricted-syntax -- view-only freshness ticker; see NOTE above
  const [, setTick] = useState(0)
  useEffect(() => {
    if (!guideOpen) return
    const id = window.setInterval(() => {
      const now = Date.now()
      const sel = selectedAiring(now)
      if (sel) {
        // follow the SELECTED block: re-anchor when it drifts out of view. When the selection IS the
        // now-airing program this tracks the broadcast; navigated away, it keeps the chosen program on
        // screen instead of snapping back to now.
        const startFrac = (sel.startMs - viewStart) / spanMs
        const endFrac = (sel.endMs - viewStart) / spanMs
        if (startFrac < 0 || endFrac > 1 || startFrac > REPIN_FRAC) setViewStart(anchorFor(sel.startMs, spanMs))
      } else {
        const frac = (now - viewStart) / spanMs
        if (frac > REPIN_FRAC || frac < 0) setViewStart(anchorFor(now, spanMs))
      }
      setTick((t) => (t + 1) % 1_000_000)
    }, 1000)
    return () => window.clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- view-only ticker; follows selection
  }, [guideOpen, viewStart, spanMs, guideSel, guideProgram])

  // Now-line: keyed by viewStart+span so it isn't restarted on every clock tick;
  // its CSS sweep (full window duration, negative delay = elapsed) is smooth.
  const nowLineStyle = useMemo(
    () => ({
      animationDuration: `${spanMs / 1000}s`,
      // eslint-disable-next-line react-hooks/purity -- live wall-clock for the now-line sweep; see NOTE above
      animationDelay: `-${Math.max(0, (Date.now() - viewStart) / 1000)}s`,
    }),
    [viewStart, spanMs],
  )

  const ticks: number[] = []
  for (let t = Math.ceil(viewStart / tickMs) * tickMs; t < viewStart + spanMs; t += tickMs) ticks.push(t)

  const visibleChannels = channels
    .map((ch, i) => ({ ch, i }))
    .filter(({ ch }) => !ch.hidden || (state.unlockedSecrets ?? []).includes(ch.number))

  // eslint-disable-next-line react-hooks/purity -- live wall-clock for now-block/now-line layout; see NOTE above
  const now = Date.now()
  // The now-line only exists when "now" is actually inside the visible window. Without this gate the
  // CSS sweep clamps a future/past "now" to the left/right edge (Math.max(0) on the delay, or
  // fill-mode:forwards holding at 100%) — drawing a live line where there is none.
  const nowFrac = (now - viewStart) / spanMs
  const nowInView = nowFrac >= 0 && nowFrac <= 1

  // ---- search (a box at the top of the guide; results derive from searchGuide) ----
  const searching = guideSearch != null
  const results = useMemo<SearchResult[]>(
    () => (searching ? searchGuide(channels, state.unlockedSecrets ?? [], guideSearch ?? '') : []),
    [searching, guideSearch, channels, state.unlockedSecrets],
  )
  const searchSel = results.length ? Math.min(Math.max(0, guideSearchSel), results.length - 1) : 0
  // eslint-disable-next-line no-restricted-syntax -- view-only ref to focus the search box; see NOTE above
  const searchRef = useRef<HTMLInputElement>(null)
  useEffect(() => { if (searching) searchRef.current?.focus() }, [searching])

  // Selecting a result: a channel goes live (its TUNE unlocks a secret); a program plays from start
  // unless it's the now-airing block. Mirrors guidePlaySearch for click parity.
  const playResult = (r: SearchResult) => {
    if (r.kind === 'channel') { tv.guidePlayChannelLive(r.channelIndex); return }
    const ch = channels[r.channelIndex]
    let isNow = false
    if (ch && !ch.noSignal) {
      const lq = buildLiveQueue(ch, state.settings.adBumpers)
      const slot = liveSlot(lq, now)
      if (slot) isNow = r.ordinal === queueIndexToProgramOrdinal(lq, slot.index)
    }
    tv.guidePlayBlock(r.channelIndex, r.ordinal, isNow)
  }
  const onSearchKey = (e: ReactKeyboardEvent) => {
    const route: Record<string, 'chUp' | 'chDown' | 'ok' | 'back'> = { ArrowUp: 'chUp', ArrowDown: 'chDown', Enter: 'ok', Escape: 'back' }
    const a = route[e.key]
    if (!a) return
    e.preventDefault()
    tv.act(a)
  }

  return (
    <div className={[styles.epg, guideOpen && styles.open].filter(Boolean).join(' ')} data-screen-label="TV Guide">
      <div className={styles.head}>
        <div className={styles.title}>TV GUIDE</div>
        <div className={styles.headRight}>
          <div className={styles.zoom}>
            <button className={styles.zoomBtn} onClick={() => tv.guideZoom(1)} title="Zoom out (more time)" disabled={guideZoom >= GUIDE_ZOOMS.length - 1}>−</button>
            <span className={styles.zoomLabel}>{Math.round(spanMs / 60000)}m</span>
            <button className={styles.zoomBtn} onClick={() => tv.guideZoom(-1)} title="Zoom in (less time)" disabled={guideZoom <= 0}>+</button>
          </div>
          <div className={styles.clock}>{timeLabel(now, false)}</div>
        </div>
      </div>

      <div className={styles.axis}>
        <div className={styles.axisCh}>CH</div>
        <div className={styles.axisTime}>
          {ticks.map((t) => (
            <span key={t} className={styles.tick} style={{ left: `${((t - viewStart) / spanMs) * 100}%` }}>
              {timeLabel(t, tickMs < 60_000)}
            </span>
          ))}
        </div>
      </div>

      {searching && (
        <div className={styles.search}>
          <span className={styles.searchIcon}>SEARCH</span>
          <input
            ref={searchRef}
            data-guide-search
            className={styles.searchInput}
            value={guideSearch ?? ''}
            placeholder="program or channel name…"
            onChange={(e) => tv.guideSearchSet(e.target.value)}
            onKeyDown={onSearchKey}
            aria-label="Search the guide"
          />
        </div>
      )}

      <div className={styles.body} ref={bodyRef}>
        {searching ? (
          <div className={styles.results}>
            {results.length === 0 ? (
              <div className={styles.searchHint}>{guideSearch ? 'No matches' : 'Type to search programs and channels'}</div>
            ) : (
              results.map((r, i) => (
                <button
                  key={r.kind === 'channel' ? `c${r.channelIndex}` : `p${r.channelIndex}:${r.ordinal}`}
                  className={[styles.result, i === searchSel && styles.resultSel].filter(Boolean).join(' ')}
                  onClick={() => playResult(r)}
                >
                  <span className={styles.resultKind}>{r.kind === 'channel' ? 'CH' : '▶'}</span>
                  <span className={styles.resultLabel}>{r.label}</span>
                  <span className={styles.resultSub}>{r.sub}</span>
                </button>
              ))
            )}
          </div>
        ) : (
        <div className={styles.rows}>
          {nowInView && <div key={`${viewStart}:${spanMs}`} className={styles.nowLine} style={nowLineStyle} />}
          {visibleChannels.map(({ ch, i }) => {
            const queue = queues.get(ch.number) ?? []
            const schedule = ch.noSignal ? [] : programsInWindow(queue, viewStart, viewStart + spanMs)
            const isSelected = i === guideSel
            const cls = [styles.row, i === channelIndex && styles.tuned, isSelected && styles.selected]
              .filter(Boolean)
              .join(' ')
            return (
              <div key={ch.number} ref={isSelected ? selRowRef : undefined} className={cls} style={{ ['--row-ident' as string]: ch.ident }}>
                <button
                  className={styles.channelCell}
                  onClick={() => tv.guidePlayChannelLive(i)}
                  title="Go live on this channel"
                >
                  <div className={styles.channelNumber}>{pad(ch.number)}</div>
                  <div className={styles.logo} dangerouslySetInnerHTML={{ __html: ch.logo || '' }} />
                </button>
                <div className={styles.timeline}>
                  {schedule.length === 0 ? (
                    <div className={styles.fallback}>
                      <span className={styles.blockTitle}>{ch.noSignal ? ch.tagline : ch.tagline || 'No schedule'}</span>
                      <span className={styles.blockTime}>{ch.schedule || ch.genre}</span>
                    </div>
                  ) : (
                    schedule.map((b) => (
                      <Block
                        key={`${b.queueIndex}@${b.startMs}`}
                        b={b}
                        queue={queue}
                        viewStart={viewStart}
                        spanMs={spanMs}
                        now={now}
                        isSelected={isSelected}
                        guideProgram={guideProgram}
                        onPlay={(ordinal, isNow) => tv.guidePlayBlock(i, ordinal, isNow)}
                      />
                    ))
                  )}
                </div>
              </div>
            )
          })}
        </div>
        )}
      </div>

      <div className={styles.foot}>
        <span>
          <kbd>↑</kbd>
          <kbd>↓</kbd> channel
        </span>
        <span>
          <kbd>←</kbd>
          <kbd>→</kbd> program
        </span>
        <span>
          <kbd>vol</kbd> +/− zoom
        </span>
        <span>
          <kbd>enter</kbd> watch
        </span>
        <span>
          <kbd>g</kbd> / <kbd>esc</kbd> close
        </span>
      </div>
    </div>
  )
}

function Block({
  b,
  queue,
  viewStart,
  spanMs,
  now,
  isSelected,
  guideProgram,
  onPlay,
}: {
  b: ScheduledItem
  queue: QueueItem[]
  viewStart: number
  spanMs: number
  now: number
  isSelected: boolean
  guideProgram: number
  onPlay: (ordinal: number, isNow: boolean) => void
}) {
  const left = Math.max(0, (b.startMs - viewStart) / spanMs)
  const right = Math.min(1, (b.endMs - viewStart) / spanMs)
  if (right <= left) return null

  const isAd = !isProgram(b.item) // a 'break' segment (injected interstitial) — the dimmed, non-navigable strip
  const isNow = b.startMs <= now && now < b.endMs
  const ordinal = isAd ? -1 : queueIndexToProgramOrdinal(queue, b.queueIndex)
  const isCursor = isSelected && !isAd && ordinal === guideProgram
  const style = { left: `${left * 100}%`, width: `${(right - left) * 100}%` }
  const cls = [styles.block, isAd && styles.adBlock, isNow && styles.nowBlock, isCursor && styles.cursorBlock]
    .filter(Boolean)
    .join(' ')

  if (isAd) {
    return (
      <div className={cls} style={style}>
        <span className={styles.blockTitle}>AD BREAK</span>
      </div>
    )
  }
  return (
    <button className={cls} style={style} onClick={() => onPlay(ordinal, isNow)}>
      <span className={styles.blockTitle}>{b.item.title || 'Untitled'}</span>
      <span className={styles.blockTime}>{timeLabel(b.startMs, spanMs <= 5 * 60_000)}</span>
    </button>
  )
}
